// „Per Post senden“ (E-POSTBUSINESS API, Edge epost-brief): Empfänger + Anschrift, optional Anschreiben auf dem
// Briefpapier (Blanko-Brief #539, Anschrift im Fenster), Anlagen dahinter – alles EIN Umschlag. Ohne Anschreiben
// setzt die Post ein Deckblatt mit der Anschrift davor. Vorschau zeigt das fertige PDF, ohne etwas zu verschicken.
import { useEffect, useState } from 'react';
import { Eye, Paperclip, PaperPlaneTilt, SpinnerGap, UserCircle, WarningCircle, X } from '@phosphor-icons/react';
import { DialogHuelle, ImmoButton, SchalterZeile, TextFeld } from '../../../../components/immobilien/ui';
import { erzeugeBlankoBriefPdf, ladeBriefpapier, ladePostSchrift } from '../../../../lib/dokumente/blankoBrief';
import { ladeBriefVorlagen, ladeEmpfaenger, type BriefVorlageKurz, type EmpfaengerDaten, type EmpfaengerTreffer } from '../../../../lib/dokumente/briefApi';
import { empfaengerWerte, vorlageZuBrief } from '../../../../lib/dokumente/briefDaten';
import { kategorieLabel } from '../../../../lib/dokumente/briefVorlagen';
import { briefVorschau, EINSCHREIBEN, ladeEpostEinstellung, sendeBrief, type BriefAuftrag, type BriefOptionen, type BriefTeil, type EpostEinstellung } from '../../../../lib/epost/epost';
import { anschriftZuEmpfaenger, blobZuBase64 } from '../../../../lib/epost/anschrift';
import { entferneAlteAblage, legeBriefInAkte } from '../../../../lib/epost/ablage';
import { EmpfaengerSuche } from '../vorlagen/BlankoBriefDialog';
import { PostLogo } from '../../../../components/epost/PostLogo';
import { offenePlatzhalter } from '../../../../lib/epost/offenePlatzhalter';

export interface PostAnlage { name: string; mime: string; laden: () => Promise<Blob>; /** Woher – wird am Brief gemerkt, damit er sich korrigiert neu senden lässt */ quelle?: { drive?: string; storage?: { bucket: string; path: string } } }

const FELD = {
  width: '100%', padding: '10px 12px', borderRadius: 14, border: 'none', outline: 'none', resize: 'vertical' as const,
  background: 'var(--fill2)', color: 'var(--ink)', fontSize: 14, lineHeight: 1.5, fontFamily: 'inherit',
};
const LABEL = { fontSize: 12, fontWeight: 700, color: 'var(--ink3)' } as const;
const ART_LABEL = { mieter: 'Mieter', eigentuemer: 'Eigentümer', kontakt: 'Kontakt', dienstleister: 'Dienstleister' } as const;
const heute = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
const grussText = 'Sehr geehrte Damen und Herren,\n\n\n\nMit freundlichen Grüßen\n\nImmobilienmanagement Schwede';
// offenePlatzhalter: lib/epost/offenePlatzhalter.ts (erkennt auch {{eingabe.x|…}})

/** Nur PDF, PNG und JPG können gedruckt werden. */
export const postFaehig = (mime: string, name = '') => /pdf|png|jpe?g/i.test(mime) || /\.(pdf|png|jpe?g)$/i.test(name);

export function PostVersandDialog({ anlagen, start, bezug, onSchliessen, onGesendet }: {
  anlagen: PostAnlage[];
  start?: { treffer?: EmpfaengerTreffer | null; anschrift?: string; betreff?: string; text?: string; mitAnschreiben?: boolean; objektId?: string | null;
    optionen?: BriefOptionen; /** Korrektur: ID des abgelehnten Briefs */ ersetzt?: string; hinweis?: string | null };
  /** Wofür der Brief steht (z. B. { typ: 'dokument', id }) – landet im Protokoll. */
  bezug?: { typ: string; id: string | null } | null;
  onSchliessen: () => void;
  onGesendet: (meldung: string) => void;
}) {
  const [einst, setEinst] = useState<EpostEinstellung | null>(null);
  const [empf, setEmpf] = useState<EmpfaengerDaten | null>(null);
  const [anschrift, setAnschrift] = useState(start?.anschrift ?? '');
  const [mitAnschreiben, setMitAnschreiben] = useState(start?.mitAnschreiben ?? anlagen.length === 0);
  const [betreff, setBetreff] = useState(start?.betreff ?? '');
  const [text, setText] = useState(start?.text ?? grussText);
  const [optionen, setOptionen] = useState<BriefOptionen>(start?.optionen ?? { farbe: false, duplex: false, einschreiben: '' });
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [vorschau, setVorschau] = useState<string | null>(null);
  const [vorlagen, setVorlagen] = useState<BriefVorlageKurz[]>([]);
  const [vorlageId, setVorlageId] = useState('');
  const [inAkte, setInAkte] = useState(true);

  useEffect(() => { void ladeEpostEinstellung().then(setEinst, () => setEinst(null)); }, []);
  useEffect(() => { void ladeBriefVorlagen().then(setVorlagen, () => setVorlagen([])); }, []);
  useEffect(() => { if (start?.treffer) void waehle(start.treffer); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (vorschau) URL.revokeObjectURL(vorschau); }, [vorschau]);


  async function waehle(t: EmpfaengerTreffer) {
    setFehler(null);
    try { const d = await ladeEmpfaenger(t); setEmpf(d); setAnschrift(d.anschrift); if (vorlageId) vorlageAnwenden(vorlageId, d); }
    catch (e) { setFehler((e as Error).message); }
  }

  /** Vorlage → Betreff + Text, Platzhalter mit den Daten des Empfängers (ohne Empfänger: neutrale Anrede, Datum). */
  function vorlageAnwenden(id: string, e: EmpfaengerDaten | null = empf) {
    setVorlageId(id);
    const v = vorlagen.find((x) => x.id === id);
    if (!v) return;
    const heute = new Date();
    const werte = e?.werte ?? { ...empfaengerWerte({}), 'datum.heute': heute.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }), 'datum.heute_lang': heute.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' }) };
    const r = vorlageZuBrief(v.html_template, werte);
    setBetreff(r.betreff); setText(r.text);
  }

  async function baueAuftrag(): Promise<BriefAuftrag> {
    if (!anschrift.trim()) throw new Error('Noch kein Empfänger: oben suchen oder die Anschrift eintippen.');
    const empfaenger = anschriftZuEmpfaenger(anschrift);
    if (!empfaenger) throw new Error('Anschrift unvollständig: letzte Zeile muss „PLZ Ort“ sein.');
    const offen = mitAnschreiben ? offenePlatzhalter(betreff, text) : [];
    if (offen.length) throw new Error(`Noch auszufüllen: ${offen.join(', ')} – bitte im Text ersetzen oder Empfänger wählen.`);
    const druckbar = anlagen.filter((a) => postFaehig(a.mime, a.name));
    if (!mitAnschreiben && druckbar.length === 0) throw new Error('Der Brief hat keinen Inhalt (nur PDF, PNG und JPG lassen sich drucken).');
    const teile: BriefTeil[] = [];
    if (mitAnschreiben) {
      const pdf = erzeugeBlankoBriefPdf({ empfaenger: anschrift, ort: 'Kirchhain', datum: heute(), betreff, text }, await ladeBriefpapier(), true, await ladePostSchrift());
      teile.push({ art: 'pdf', base64: pdf.output('datauristring').replace(/^data:[^,]*,/, ''), name: 'Anschreiben.pdf' });
    }
    for (const a of druckbar) {
      const blob = await a.laden();
      const istPdf = /pdf/i.test(a.mime) || /\.pdf$/i.test(a.name);
      teile.push({ art: istPdf ? 'pdf' : 'bild', base64: await blobZuBase64(blob), mime: istPdf ? 'application/pdf' : (a.mime || 'image/jpeg'), name: a.name });
    }
    const empfBezug = empf ? { bezug_typ: empf.art, bezug_id: empf.id } : {};
    const empfIds = empf ? { empfaenger_typ: empf.art, empfaenger_id: empf.id } : {};
    return {
      empfaenger, teile, anschrift_im_dokument: mitAnschreiben, optionen,
      betreff: betreff || anlagen[0]?.name?.replace(/\.[^.]+$/, '') || 'Brief',
      objekt_id: empf?.objektId ?? start?.objektId ?? null,
      ...(bezug ? { bezug_typ: bezug.typ, bezug_id: bezug.id } : empfBezug),
      ...empfIds,
      entwurf: { anschrift, betreff, text, mitAnschreiben, optionen,
        anlagen: druckbar.map((a) => ({ name: a.name, mime: a.mime, ...(a.quelle?.drive ? { drive: a.quelle.drive } : {}), ...(a.quelle?.storage ? { storage: a.quelle.storage } : {}) })) },
      ersetzt_id: start?.ersetzt ?? null,
    };
  }

  const zeigeVorschau = async () => {
    setLaeuft(true); setFehler(null);
    try {
      const r = await briefVorschau(await baueAuftrag());
      const bytes = Uint8Array.from(atob(r.pdf_base64), (c) => c.charCodeAt(0));
      setVorschau(URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' })));
    } catch (e) { setFehler((e as Error).message); }
    finally { setLaeuft(false); }
  };

  const senden = async () => {
    setLaeuft(true); setFehler(null);
    try {
      const auftrag = await baueAuftrag();
      const r = await sendeBrief(auftrag, inAkte);
      let ablage = '';
      const brief = r.briefe[0];
      if (inAkte && brief?.pdf_base64) {
        try {
          const a = await legeBriefInAkte({ briefId: brief.id, pdfBase64: brief.pdf_base64, betreff: auftrag.betreff ?? 'Brief', objektId: auftrag.objekt_id ?? null,
            art: empf?.art ?? null, empfaengerId: empf?.id ?? null, ordnerName: empf?.ordnerName ?? null,
            empfaengerName: empf?.name ?? auftrag.empfaenger.zeilen[0] ?? null });
          ablage = a.ok ? ` In der Akte abgelegt (${a.pfad}).` : ` Nicht in der Akte abgelegt: ${a.grund}.`;
        } catch (e) { ablage = ` Nicht in der Akte abgelegt: ${(e as Error).message}.`; }
      }
      if (start?.ersetzt) await entferneAlteAblage(start.ersetzt);
      onGesendet((r.test
        ? `Testbrief eingeliefert – nichts wird gedruckt, die Vorschau kommt per E-Mail${einst?.test_email ? ` an ${einst.test_email}` : ''}.`
        : 'Brief an die Post übergeben.') + ablage);
    } catch (e) { setFehler((e as Error).message); setLaeuft(false); }
  };

  const aus = einst !== null && !einst.aktiv;

  return (
    <DialogHuelle titel={start?.ersetzt ? 'Brief korrigieren und neu senden' : 'Per Post senden'} untertitel="Die Post druckt, kuvertiert, frankiert und stellt zu" icon={PostLogo}
      onSchliessen={onSchliessen} laeuft={laeuft} breite={vorschau ? 1440 : 720}
      fuss={
        <div className="flex w-full flex-wrap justify-end" style={{ gap: 8 }}>
          <ImmoButton icon={laeuft ? SpinnerGap : Eye} onClick={() => void zeigeVorschau()} disabled={laeuft}>Vorschau</ImmoButton>
          <ImmoButton art="akzent" icon={PaperPlaneTilt} onClick={() => void senden()} disabled={laeuft || aus}>
            {laeuft ? 'Wird gesendet …' : 'An die Post senden'}
          </ImmoButton>
        </div>
      }>
      <div className="immo-scroll grid overflow-y-auto" style={{ gap: 16, gridTemplateColumns: vorschau ? 'minmax(0,5fr) minmax(0,7fr)' : 'minmax(0,1fr)' }}>
        <div className="flex flex-col" style={{ gap: 12 }}>
          {start?.hinweis && (
            <span role="status" style={{ fontSize: 12.5, padding: '10px 12px', borderRadius: 14, background: 'var(--warn-soft)', color: 'var(--ink2)' }}>{start.hinweis}</span>
          )}
          {einst?.modus === 'live' ? (
            <span role="status" style={{ fontSize: 12.5, fontWeight: 700, padding: '10px 12px', borderRadius: 14, background: 'var(--bad-soft)', color: 'var(--bad)' }}>
              LIVE – dieser Brief wird gedruckt, zugestellt und kostet Porto.
            </span>
          ) : einst && (
            <span role="status" style={{ fontSize: 12.5, fontWeight: 700, padding: '10px 12px', borderRadius: 14, background: 'var(--ok-soft)', color: 'var(--ok)' }}>
              Testmodus – nichts wird gedruckt, die Vorschau kommt per E-Mail{einst.test_email ? ` an ${einst.test_email}` : ''}.
            </span>
          )}
          {aus && (
            <span role="status" style={{ fontSize: 12.5, padding: '10px 12px', borderRadius: 14, background: 'var(--warn-soft, var(--fill2))', color: 'var(--ink2)' }}>
              Der Briefversand ist im Control Center ausgeschaltet. Die Vorschau geht trotzdem.
            </span>
          )}
          <div className="flex flex-col" style={{ gap: 6 }}>
            <span style={LABEL}>Empfänger</span>
            {empf ? (
              <div className="flex items-center" style={{ gap: 8, padding: '6px 6px 6px 12px', borderRadius: 14, background: 'var(--acc-soft)' }}>
                <UserCircle size={18} weight="fill" color="var(--acc)" />
                <span className="min-w-0 flex-1 truncate" style={{ fontSize: 13.5, fontWeight: 700 }}>{empf.name} <span style={{ fontWeight: 600, color: 'var(--ink3)' }}>· {ART_LABEL[empf.art]}</span></span>
                <button type="button" aria-label="Empfänger lösen" data-tip="Anderen Empfänger wählen" onClick={() => setEmpf(null)}
                  className="flex items-center justify-center" style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: 'transparent', color: 'var(--ink2)', cursor: 'pointer' }}>
                  <X size={13} weight="bold" />
                </button>
              </div>
            ) : <EmpfaengerSuche onWahl={(t) => void waehle(t)} />}
            <textarea rows={4} value={anschrift} onChange={(e) => setAnschrift(e.target.value)} aria-label="Anschrift"
              placeholder="Oben suchen – oder Anschrift hier eintippen (letzte Zeile: PLZ Ort)" style={FELD} />
          </div>

          <SchalterZeile an={mitAnschreiben} onChange={setMitAnschreiben} label="Anschreiben auf Briefpapier voranstellen"
            zusatz={mitAnschreiben ? 'Anschrift steht im Fenster' : 'Ohne Anschreiben setzt die Post ein Deckblatt mit Anschrift davor (eine Seite mehr)'} />
          {mitAnschreiben && (
            <>
              <label className="flex flex-col" style={{ gap: 6 }}>
                <span style={LABEL}>Vorlage</span>
                <select value={vorlageId} onChange={(e) => (e.target.value ? vorlageAnwenden(e.target.value) : setVorlageId(''))}
                  style={{ ...FELD, height: 44, padding: '0 12px', cursor: 'pointer' }}>
                  <option value="">Freier Text</option>
                  {vorlagen.map((v) => <option key={v.id} value={v.id}>{v.name} ({kategorieLabel(v.kategorie)})</option>)}
                </select>
                {vorlageId && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Platzhalter sind {empf ? `mit den Daten von ${empf.name} gefüllt` : 'gefüllt, sobald ein Empfänger gewählt ist'}. Alles bleibt änderbar. Neue Vorlagen: Dokumente › Vorlagen.</span>}
              </label>
              <TextFeld label="Betreff" wert={betreff} onChange={setBetreff} placeholder="z. B. Ihre Wohnung Musterstraße 1, EG links" />
              <label className="flex flex-col" style={{ gap: 6 }}>
                <span style={LABEL}>Text</span>
                <textarea rows={10} value={text} onChange={(e) => setText(e.target.value)} style={FELD} />
              </label>
            </>
          )}

          {anlagen.length > 0 && (
            <div className="flex flex-col" style={{ gap: 6 }}>
              <span style={LABEL}>Im selben Umschlag</span>
              {anlagen.map((a) => (
                <span key={a.name} className="flex items-center" style={{ gap: 8, fontSize: 13, color: postFaehig(a.mime, a.name) ? 'var(--ink)' : 'var(--bad)' }}>
                  <Paperclip size={14} />{a.name}{!postFaehig(a.mime, a.name) && ' – nicht druckbar, bleibt weg'}
                </span>
              ))}
            </div>
          )}

          <div className="flex flex-col" style={{ gap: 6 }}>
            <span style={LABEL}>Druck und Versand</span>
            <SchalterZeile klein an={!!optionen.farbe} onChange={(v) => setOptionen((o) => ({ ...o, farbe: v }))} label="Farbdruck" />
            <SchalterZeile klein an={!!optionen.duplex} onChange={(v) => setOptionen((o) => ({ ...o, duplex: v }))} label="Beidseitig drucken" />
            <SchalterZeile klein an={inAkte} onChange={setInAkte} label="Brief als PDF in die Akte legen"
              zusatz={empf?.objektId || start?.objektId ? 'In den Ordner des Empfängers im Objekt, sonst 01 Verwaltung/Mieter bzw. Eigentümer' : 'Ohne Objekt: in den Ordner „Postausgang – Briefe ohne Objekt“ (Aktenschrank)'} />
            <select value={optionen.einschreiben ?? ''} aria-label="Einschreiben"
              onChange={(e) => setOptionen((o) => ({ ...o, einschreiben: e.target.value as BriefOptionen['einschreiben'] }))}
              style={{ ...FELD, height: 44, padding: '0 12px', cursor: 'pointer' }}>
              <option value="">Normaler Brief</option>
              {EINSCHREIBEN.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>

          {fehler && (
            <span role="alert" className="flex items-start" style={{ gap: 6, fontSize: 12.5, color: 'var(--bad)' }}>
              <WarningCircle weight="fill" size={14} style={{ flexShrink: 0, marginTop: 1 }} />{fehler}
            </span>
          )}
        </div>
        {vorschau && (
          <iframe title="Vorschau Brief" src={`${vorschau}#view=FitH`} className="w-full" style={{ height: 'calc(100dvh - 200px)', minHeight: 560, border: 'none', borderRadius: 14, background: '#fff', position: 'sticky', top: 0 }} />
        )}
      </div>
    </DialogHuelle>
  );
}
